import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  type Article,
  type GeoNeighborhoodAnalysis,
  type GeoPlannerPastRun,
  type GeoPlannerResult,
  type GeoTargetServiceRow,
  getGeoPlannerRun,
  importGeoPlannerToBuildQueue,
  listArticles,
  listGeoPlannerRuns,
  runGeoPlannerResearch,
} from "../lib/api";
import { Button, Field, Select, TextInput } from "../components/Form";
import type { WorkspaceContext } from "./ClientWorkspacePage";

/**
 * Geo Relevance Planner tab.
 *
 *   - User enters Target Service + City
 *   - Single click ("Generate Places") runs the entire pipeline:
 *       1. Pulls candidate neighborhoods + landmarks from the (stubbed) Places API
 *       2. Sends them to Claude with the client context for ranking analysis
 *       3. Returns scored neighborhoods + flat target-services table
 *   - Past runs cached per (client, target service)
 *   - Selected target services can be sent to Build Queue as location_page articles
 */
export default function GeoPlannerTab() {
  const { client } = useOutletContext<WorkspaceContext>();
  const navigate = useNavigate();

  const initialCity = parseCityFromAddress(client.businessAddress);
  // fix660: geo pages are practice-level, so default the keyword to "dentist"
  // (the general practice term) rather than a specific service.
  const [targetService, setTargetService] = useState("dentist");
  const [city, setCity] = useState(initialCity);
  const [pastRuns, setPastRuns] = useState<GeoPlannerPastRun[]>([]);
  const [running, setRunning] = useState(false);
  // fix676: elapsed-time counter shown while a scan runs, so "Generating..."
  // isn't a silent black box. The scan is genuinely slow (Census + Places +
  // per-area Sonnet scoring), typically 1-3 minutes.
  const [elapsedSec, setElapsedSec] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GeoPlannerResult | null>(null);

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  // fix704: names of areas that ALREADY have a geo_page article (sent to Content
  // Generation). Used to pre-select only the rows NOT yet sent, so re-visiting
  // the tab doesn't re-select areas already in the build queue. Matched by the
  // area/target-service name (lowercased), which is what the import writes to
  // articles.target_service.
  const [sentAreaNames, setSentAreaNames] = useState<Set<string>>(new Set());

  // Load existing geo articles so we know which areas are already sent.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const arts: Article[] = await listArticles(client.id);
        if (cancelled) return;
        const names = new Set<string>();
        for (const a of arts) {
          if (a.contentType === "geo_page" && a.targetService) {
            names.add(a.targetService.trim().toLowerCase());
          }
        }
        setSentAreaNames(names);
      } catch (err) {
        // Non-fatal: if this fails we just fall back to selecting all rows.
        console.error(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client.id]);

  // Given the full set of rows, return the keys that should be pre-selected:
  // every row whose area has NOT already been sent to Content Generation.
  const unsentKeys = (rows: GeoTargetServiceRow[]): Set<string> =>
    new Set(
      rows
        .filter(
          (t) => !sentAreaNames.has((t.targetService ?? "").trim().toLowerCase())
        )
        .map((t) => keyForRow(t))
    );

  // fix704: the existing-articles fetch is async and may finish AFTER a result
  // has already pre-selected all rows. When sentAreaNames arrives, drop any
  // already-sent rows from the current selection once — keyed on the sent set +
  // result identity so it doesn't fight the user's manual toggles afterward.
  const reconciledFor = useRef<string>("");
  useEffect(() => {
    if (!result || sentAreaNames.size === 0) return;
    const stamp = `${result.city}:${result.targetServices.length}:${sentAreaNames.size}`;
    if (reconciledFor.current === stamp) return;
    reconciledFor.current = stamp;
    setSelectedKeys((prev) => {
      const next = new Set<string>();
      for (const row of result.targetServices) {
        const k = keyForRow(row);
        if (!prev.has(k)) continue;
        if (sentAreaNames.has((row.targetService ?? "").trim().toLowerCase()))
          continue; // drop already-sent
        next.add(k);
      }
      return next;
    });
  }, [result, sentAreaNames]);

  // Load past runs on mount, and auto-restore the most recent scan so leaving
  // this tab and coming back shows the last run instead of an empty form.
  // fix665: the component unmounts on tab switch (in-memory state is lost), but
  // runs are persisted server-side — so we re-hydrate from the newest run.
  useEffect(() => {
    let cancelled = false;
    listGeoPlannerRuns(client.id)
      .then(async (data) => {
        if (cancelled) return;
        setPastRuns(data.runs);
        // Auto-load the newest run's full result if we don't already have one
        // showing (runs come back ORDER BY updated_at DESC).
        const newest = data.runs[0];
        if (newest && !result) {
          try {
            const full = await getGeoPlannerRun(client.id, newest.targetService);
            if (cancelled) return;
            if (full.result) {
              setResult(full.result);
              setTargetService(newest.targetService);
              setCity(full.result.city);
              setSelectedKeys(unsentKeys(full.result.targetServices));
            }
          } catch (err) {
            // Non-fatal: leave the form empty if the restore fails.
            console.error(err);
          }
        }
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
    // Intentionally keyed on client.id only — we restore once per client mount,
    // not on every result change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  // Resync city when client changes
  useEffect(() => {
    setCity(parseCityFromAddress(client.businessAddress));
  }, [client.id, client.businessAddress]);

  // fix676: tick the elapsed-time counter once per second while a scan runs.
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setElapsedSec((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  async function handleRun() {
    if (!targetService.trim() || !city.trim()) {
      setError("Target Service and City are required.");
      return;
    }
    setError(null);
    setElapsedSec(0);
    setRunning(true);
    setSelectedKeys(new Set());
    try {
      const data = await runGeoPlannerResearch(client.id, {
        targetService: targetService.trim(),
        city: city.trim(),
      });
      setResult(data.result);
      // fix704: pre-select only rows NOT already sent to Content Generation.
      setSelectedKeys(unsentKeys(data.result.targetServices));
      // Refresh past runs list
      const runs = await listGeoPlannerRuns(client.id);
      setPastRuns(runs.runs);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Research failed.");
    } finally {
      setRunning(false);
    }
  }

  async function handleLoadPastRun(term: string) {
    if (!term) return;
    setError(null);
    try {
      const data = await getGeoPlannerRun(client.id, term);
      if (data.result) {
        setResult(data.result);
        setTargetService(term);
        setCity(data.result.city);
        setSelectedKeys(unsentKeys(data.result.targetServices));
      } else {
        setError("Past run not found.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load past run.");
    }
  }

  function toggleRow(key: string) {
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedKeys(next);
  }

  function selectAll() {
    if (!result) return;
    setSelectedKeys(new Set(result.targetServices.map((t) => keyForRow(t))));
  }

  function unselectAll() {
    setSelectedKeys(new Set());
  }

  function clearResults() {
    setResult(null);
    setSelectedKeys(new Set());
  }

  async function handleSendToBuildQueue() {
    if (!result || selectedKeys.size === 0) return;
    setImporting(true);
    setError(null);
    try {
      const selected = result.targetServices.filter((t) =>
        selectedKeys.has(keyForRow(t))
      );
      await importGeoPlannerToBuildQueue(
        client.id,
        selected.map((t) => ({
          targetService: t.targetService,
          targetLandmark: t.targetLandmark,
          neighborhoodName: t.neighborhoodName,
          distanceLabel: t.distanceLabel,
        }))
      );
      navigate(`/clients/${client.id}/content-generation`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed.");
      setImporting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-bg-subtle border border-border flex items-center justify-center text-text-muted shrink-0">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
        </div>
        <div>
          <h1 className="text-text font-semibold text-xl">Geo Relevance Planner</h1>
          <p className="text-text-muted text-sm mt-0.5">
            Scan the area around the practice and plan a geo page cluster (one "Dentist Near [Area]" page per high-opportunity neighborhood).
          </p>
        </div>
      </div>

      {/* Input card */}
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
        <h2 className="text-text font-semibold mb-3">Configure</h2>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-sm text-text font-medium mb-1.5">
              Practice Keyword
            </label>
            <TextInput
              value={targetService}
              onChange={setTargetService}
              placeholder="e.g. dentist"
            />
          </div>
          <div>
            <label className="block text-sm text-text font-medium mb-1.5">
              City
            </label>
            <TextInput
              value={city}
              onChange={setCity}
              placeholder="e.g. Dallas"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              onClick={handleRun}
              disabled={running || !targetService.trim() || !city.trim()}
            >
              {running
                ? `↻ Generating... ${formatElapsed(elapsedSec)}`
                : "📍 Generate Places"}
            </Button>
            {running && (
              <span className="text-xs text-text-muted whitespace-nowrap">
                ~1–3 min
              </span>
            )}
          </div>
        </div>
        {pastRuns.length > 0 && (
          <div className="mt-3">
            <Field label="Past runs">
              <Select
                value=""
                onChange={(v) => handleLoadPastRun(v)}
                options={[
                  { value: "", label: "Past runs..." },
                  ...pastRuns.map((r) => ({
                    value: r.targetService,
                    label: `${r.targetService} in ${r.city} (${r.targetServiceCount})`,
                  })),
                ]}
              />
            </Field>
          </div>
        )}
        {error && <p className="text-danger text-sm mt-3">{error}</p>}
      </div>

      {/* Stat strip */}
      {result && result.dataSource === "stub" && (
        <div className="bg-warning-bg border border-warning-border rounded-lg px-4 py-3 text-sm text-text flex items-start gap-2">
          <span className="shrink-0">⚠️</span>
          <div>
            <span className="font-semibold">Placeholder data — not a real scan.</span>{" "}
            These area names are generic fallbacks, not real neighborhoods from Google.
            {result.fallbackReason ? (
              <span className="text-text-muted"> Reason: {result.fallbackReason}</span>
            ) : null}{" "}
            Set a working Google Places API (New) key in Settings and re-run to get real
            local areas. Use the Test button there to confirm the key reaches searchNearby.
          </div>
        </div>
      )}

      {result && (
        <div className="bg-bg-card border border-border rounded-lg px-5 py-3 shadow-card flex items-center gap-6 text-sm">
          <span>
            <span className="text-text-muted">Grid points: </span>
            <span className="text-text font-semibold">{result.gridPoints}</span>
          </span>
          <span>
            <span className="text-text-muted">Competitors: </span>
            <span className="text-text font-semibold">{result.competitorCount}</span>
          </span>
          <span>
            <span className="text-text-muted">City: </span>
            <span className="text-text font-semibold">{result.city}</span>
          </span>
        </div>
      )}

      {/* fix666: the Geo Page Plan (the actionable table) is now the PRIMARY
          view and comes first; the AI Neighborhood Analysis (the reasoning /
          why) moves below and is collapsed by default. */}

      {/* Target Services table */}
      {result && result.targetServices.length > 0 && (
        <TargetServicesTable
          rows={result.targetServices}
          selectedKeys={selectedKeys}
          onToggleRow={toggleRow}
          onSelectAll={selectAll}
          onUnselectAll={unselectAll}
          onClearResults={clearResults}
          onSend={handleSendToBuildQueue}
          importing={importing}
        />
      )}

      {/* Neighborhood Analysis (collapsed by default) */}
      {result && result.neighborhoods.length > 0 && (
        <NeighborhoodAccordion neighborhoods={result.neighborhoods} />
      )}

      {result &&
        result.neighborhoods.length === 0 &&
        result.targetServices.length === 0 && (
          <div className="bg-bg-card border border-border rounded-lg p-8 text-center shadow-card">
            <p className="text-text-muted text-sm">
              Analysis came back empty — try a more specific service term or a different city.
            </p>
          </div>
        )}
    </div>
  );
}

// =========================================================================
// Section: AI Neighborhood Analysis
// =========================================================================
function NeighborhoodAccordion({
  neighborhoods,
}: {
  neighborhoods: GeoNeighborhoodAnalysis[];
}) {
  // fix666: the whole section starts COLLAPSED — the Geo Page Plan above is the
  // primary view; this reasoning panel is opened on demand. Individual rows also
  // start closed (no auto-open first row).
  const [sectionOpen, setSectionOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div>
      <button
        type="button"
        onClick={() => setSectionOpen((v) => !v)}
        className="w-full flex items-center gap-2 text-left mb-2 group"
      >
        <span className="text-purple-500">✨</span>
        <span className="text-text font-semibold">AI Neighborhood Analysis</span>
        <span className="text-text-muted text-sm font-normal">
          {(() => {
            const total = neighborhoods.length;
            const cities = neighborhoods.filter((n) => n.areaType === "city").length;
            const hoods = total - cities;
            // "9 areas: 3 city, 6 neighborhood" — only show a part when it's non-zero.
            const parts: string[] = [];
            if (cities > 0) parts.push(`${cities} city`);
            if (hoods > 0) parts.push(`${hoods} neighborhood${hoods === 1 ? "" : "s"}`);
            return `${total} area${total === 1 ? "" : "s"}${
              parts.length ? `: ${parts.join(", ")}` : ""
            }`;
          })()}
        </span>
        <span className="ml-auto text-text-muted text-sm group-hover:text-text">
          {sectionOpen ? "Hide ▴" : "Show reasoning ▾"}
        </span>
      </button>
      {sectionOpen && (
        <div className="space-y-2">
          {neighborhoods.map((n) => (
            <NeighborhoodRow
              key={n.name}
              neighborhood={n}
              expanded={expanded === n.name}
              onToggle={() => setExpanded(expanded === n.name ? null : n.name)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NeighborhoodRow({
  neighborhood: n,
  expanded,
  onToggle,
}: {
  neighborhood: GeoNeighborhoodAnalysis;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="bg-bg-card border border-border rounded-lg shadow-card overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-bg-hover/40 text-left"
      >
        <ScoreBadge score={n.score} />
        <span className="text-text font-semibold">{n.name}</span>
        {n.areaType === "city" ? (
          <span className="bg-blue-100 text-blue-700 border border-blue-200 text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
            🏙 City
          </span>
        ) : (
          <span className="bg-teal-100 text-teal-700 border border-teal-200 text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
            📍 Neighborhood
          </span>
        )}
        <span className="bg-accent-subtle text-accent text-xs font-medium px-2 py-0.5 rounded">
          {n.distanceLabel}
        </span>
        <span className="ml-auto text-text-muted">{expanded ? "▴" : "▾"}</span>
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-border-subtle bg-bg-subtle/30">
          <p className="text-text text-sm leading-relaxed">{n.narrative}</p>
          <div className="flex flex-wrap gap-4 mt-3 text-xs">
            <span>
              <span className="text-text-muted">Improvement: </span>
              <span className="text-text font-medium">{n.improvement}</span>
            </span>
            <span>
              <span className="text-text-muted">Ease of ranking: </span>
              <span className="text-accent font-medium">{n.easeOfRanking}/10</span>
            </span>
            <span>
              <span className="text-text-muted">Traffic potential: </span>
              <span className="text-purple-500 font-medium">{n.trafficPotential}/10</span>
            </span>
          </div>
          {n.contentTopics.length > 0 && (
            <div className="mt-3">
              <p className="text-text-muted text-xs uppercase tracking-wide mb-1.5">
                Content topics:
              </p>
              <ul className="space-y-1 ml-3">
                {n.contentTopics.map((t, i) => (
                  <li key={i} className="text-text text-sm">
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Section: Target Services table
// =========================================================================
function TargetServicesTable({
  rows,
  selectedKeys,
  onToggleRow,
  onSelectAll,
  onUnselectAll,
  onClearResults,
  onSend,
  importing,
}: {
  rows: GeoTargetServiceRow[];
  selectedKeys: Set<string>;
  onToggleRow: (key: string) => void;
  onSelectAll: () => void;
  onUnselectAll: () => void;
  onClearResults: () => void;
  onSend: () => void;
  importing: boolean;
}) {
  const allSelected = rows.length > 0 && selectedKeys.size === rows.length;

  return (
    <div className="bg-bg-card border border-border rounded-lg shadow-card">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <div className="flex items-center gap-3">
          <h2 className="text-text font-semibold">Geo Page Plan</h2>
          <span className="text-text-muted text-sm">
            {selectedKeys.size} of {rows.length} selected
          </span>
          <span className="text-accent text-xs flex items-center gap-1" title="Ranked by AI">
            ⓘ AI-prioritized
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={onClearResults}>
            🗑 Clear Results
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={onSelectAll}
            disabled={allSelected}
          >
            Select All
          </Button>
          <Button variant="secondary" size="sm" onClick={onUnselectAll}>
            Unselect All
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onSend}
            disabled={selectedKeys.size === 0 || importing}
          >
            {importing
              ? "Importing..."
              : `→ Send to Content Generation${selectedKeys.size > 0 ? ` (${selectedKeys.size})` : ""}`}
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg-subtle border-b border-border-subtle text-text-muted text-xs uppercase tracking-wide">
            <tr>
              <th className="w-10 px-3 py-2.5"></th>
              <th className="text-left px-3 py-2.5 w-20">Priority</th>
              <th className="text-left pl-6 pr-3 py-2.5">Neighborhood</th>
              <th className="text-left px-3 py-2.5 w-28">Type</th>
              <th className="text-left pl-6 pr-3 py-2.5">Geo Page</th>
              <th className="text-left px-3 py-2.5 w-24">Distance</th>
              <th className="w-10 px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const key = keyForRow(row);
              const selected = selectedKeys.has(key);
              return (
                <tr
                  key={key}
                  className="border-b border-border-subtle last:border-b-0 hover:bg-bg-hover/40"
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => onToggleRow(key)}
                      className="accent-accent"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <ScoreBadge score={row.priority} compact />
                  </td>
                  <td className="pl-6 pr-3 py-2 text-text-muted">{row.neighborhoodName}</td>
                  <td className="px-3 py-2">
                    {row.areaType === "city" ? (
                      <span className="bg-blue-100 text-blue-700 border border-blue-200 text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
                        🏙 City
                      </span>
                    ) : (
                      <span className="bg-teal-100 text-teal-700 border border-teal-200 text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
                        📍 Neighborhood
                      </span>
                    )}
                  </td>
                  <td className="pl-6 pr-3 py-2 text-text font-medium">{row.targetService}</td>
                  <td className="px-3 py-2">
                    <span className="bg-accent-subtle text-accent text-xs font-medium px-2 py-0.5 rounded">
                      {row.distanceLabel}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => onToggleRow(key)}
                      className="text-text-subtle hover:text-danger"
                      title="Remove from selection"
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =========================================================================
// Helpers
// =========================================================================
function ScoreBadge({ score, compact = false }: { score: number; compact?: boolean }) {
  let classes = "bg-bg-subtle text-text-muted border-border";
  if (score >= 8) {
    classes = "bg-success-bg text-success border-success-border";
  } else if (score >= 6) {
    classes = "bg-warning-bg text-warning border-warning-border";
  } else if (score >= 4) {
    classes = "bg-warning-bg/50 text-warning border-warning-border";
  }
  if (compact) {
    return (
      <span
        className={`text-xs font-bold px-2 py-0.5 rounded border w-7 inline-block text-center ${classes}`}
      >
        {score}
      </span>
    );
  }
  return (
    <span
      className={`text-xs font-bold px-2 py-1 rounded border w-12 text-center flex-shrink-0 ${classes}`}
    >
      {score}/10
    </span>
  );
}

/** fix676: format elapsed seconds as m:ss (or just seconds under a minute). */
function formatElapsed(totalSec: number): string {
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function keyForRow(row: GeoTargetServiceRow): string {
  return `${row.neighborhoodName}::${row.targetService}::${row.targetLandmark}`;
}

function parseCityFromAddress(address: string | null): string {
  if (!address) return "";
  // fix282: handle two-line addresses (newline between suite and city) so the
  // suite and city don't get mashed ("...#270Charlotte"). Split on newlines too.
  const parts = address
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length < 2) return "";
  const city = parts[parts.length - 2] ?? "";
  const stateZip = parts[parts.length - 1] ?? "";
  const stateMatch = stateZip.match(/^([A-Z]{2})/);
  if (city && stateMatch) {
    return `${city}, ${stateMatch[1]}`;
  }
  return city;
}
